import { useEffect, ReactNode, useState } from "react";
import { PublicClientApplication } from "@azure/msal-browser";
import { MsalProvider, useMsal, useIsAuthenticated } from "@azure/msal-react";
import { AuthProvider, useAuth } from "./context/AuthContext";
import * as authTypes from "./constants/authTypes";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import Unauthorised from "./components/Unauthorised";
import { hostingUrl } from "./utils/browserFunctions";

interface B2BAuthConfig {
  auth: {
    clientId: string;
    authority: string;
    redirectUri: string;
    postLogoutRedirectUri: string;
  };
  cache: {
    cacheLocation: "sessionStorage";
    storeAuthStateInCookie: boolean;
  };
}

interface B2BWrapperProps {
  children: ReactNode;
}

const { REACT_APP_B2B_CLIENT_ID, REACT_APP_B2B_AUTHORITY } = process.env;

if (!REACT_APP_B2B_CLIENT_ID || !REACT_APP_B2B_AUTHORITY) {
  throw new Error("Missing config");
}

const b2bAuthConfig: B2BAuthConfig = {
  auth: {
    clientId: REACT_APP_B2B_CLIENT_ID,
    authority: REACT_APP_B2B_AUTHORITY,
    redirectUri: hostingUrl(),
    postLogoutRedirectUri: hostingUrl(),
  },
  cache: {
    cacheLocation: "sessionStorage",
    storeAuthStateInCookie: false,
  },
};

const msalInstance = new PublicClientApplication(b2bAuthConfig);

const ProtectedContent = ({ children }: B2BWrapperProps) => {
  const isAuthenticated = useIsAuthenticated();
  const { inProgress } = useMsal();
  const { login } = useAuth();

  useEffect(() => {
    if (!isAuthenticated && inProgress === "none") {
      login();
    }
  }, [isAuthenticated, inProgress]);

  if (!isAuthenticated && inProgress !== "none") {
    return <></>;
  }

  return <>{children}</>;
};

const B2BWrapper = ({ children }: B2BWrapperProps) => {
  const [initialized, setInitialized] = useState(false);

  useEffect(() => {
    msalInstance.initialize().then(() => {
      setInitialized(true);
    });
  }, []);

  if (!initialized) {
    return <></>;
  }
  return (
    <BrowserRouter>
      <MsalProvider instance={msalInstance}>
        <AuthProvider authType={authTypes.B2B}>
          <Routes>
            <Route path="/unauthorised" element={<Unauthorised />} />
            <Route path="/" element={<ProtectedContent>{children}</ProtectedContent>} />
          </Routes>
        </AuthProvider>
      </MsalProvider>
    </BrowserRouter>
  );
};

export default B2BWrapper;
